'use client';

import { type MotionValue, useMotionValueEvent } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import { Slider } from '@/components/ui/slider';
import { playSfx } from '@/hooks/use-sound';

import { SEGMENTS_BEFORE, TOTAL_BEFORE } from '../data';

/**
 * Scrub through simulated time. Playback drives the thumb (motion-value
 * subscription); dragging seeks the clock. A tick sound fires only when the
 * thumb crosses a segment boundary of the before track.
 */

// Cumulative segment edges, for the scrub tick sound.
const BOUNDARIES: number[] = [];
{
  let acc = 0;
  for (const segment of SEGMENTS_BEFORE) {
    acc += segment.seconds;
    BOUNDARIES.push(acc);
  }
}

function crossedBoundary(from: number, to: number): boolean {
  const [lo, hi] = from < to ? [from, to] : [to, from];
  return BOUNDARIES.some((edge) => edge > lo && edge <= hi);
}

export function RaceScrubber({
  simTime,
  onScrubStart,
  onScrubEnd,
}: {
  simTime: MotionValue<number>;
  onScrubStart: (seconds: number) => void;
  onScrubEnd: () => void;
}) {
  const [displayTime, setDisplayTime] = useState(0);
  const scrubbingRef = useRef(false);
  const lastScrubRef = useRef(0);
  const endTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useMotionValueEvent(simTime, 'change', (t) => {
    if (scrubbingRef.current) return;
    const rounded = Math.round(t);
    setDisplayTime((current) => (current === rounded ? current : rounded));
  });

  useEffect(
    () => () => {
      if (endTimerRef.current) clearTimeout(endTimerRef.current);
    },
    []
  );

  /**
   * Radix cannot be trusted to bracket a gesture: keyboard steps fire
   * onValueCommit *before* onValueChange, and a pointer drag that lands back
   * on its start value never commits at all. Either case would strand
   * `scrubbing` as true and permanently detach the thumb from playback, so
   * the scrub always ends on a quiet timer, with commit as a fast path.
   */
  const endScrubSoon = () => {
    if (endTimerRef.current) clearTimeout(endTimerRef.current);
    endTimerRef.current = setTimeout(() => {
      endTimerRef.current = null;
      if (!scrubbingRef.current) return;
      scrubbingRef.current = false;
      onScrubEnd();
    }, 250);
  };

  return (
    <div
      className="flex items-center gap-3 border-b border-edge px-3 py-2.5"
      data-sfx-managed=""
    >
      <span className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
        scrub
      </span>
      <Slider
        min={0}
        max={TOTAL_BEFORE}
        step={1}
        value={[displayTime]}
        aria-label="Scrub simulated time"
        onValueChange={([value]) => {
          if (!scrubbingRef.current) {
            scrubbingRef.current = true;
            lastScrubRef.current = displayTime;
            onScrubStart(value);
          }
          if (crossedBoundary(lastScrubRef.current, value)) {
            playSfx('key');
          }
          lastScrubRef.current = value;
          setDisplayTime(value);
          simTime.set(value);
          endScrubSoon();
        }}
        onValueCommit={() => {
          if (!scrubbingRef.current) return;
          endScrubSoon();
        }}
      />
      <span className="w-10 text-right font-mono text-xs text-muted-foreground tabular-nums">
        {displayTime}s
      </span>
    </div>
  );
}
